import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Autocomplete,
  LinearProgress,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
} from "@mui/icons-material";
import { useRouter } from "../../../hooks/useRouter";
import { formatCurrency } from "../../../utils";
import FinanceContainer from "../containers/FinanceContainer";
import config from "../../../config";

interface Debtor {
  id: string;
  fullName: string;
  unitRef?: string;
  developmentName?: string;
  purchasePrice: number;
  depositPaid: number;
  monthlyInstalment: number;
  interestRate: number;
  firstPaymentDate?: string;
}

interface AmortisationEntry {
  id: string;
  debtorId: string;
  periodDate: string;
  openingBalance: number;
  instalment: number;
  interest: number;
  principal: number;
  closingBalance: number;
  paid: boolean;
  amountPaid: number;
  paidDate?: string;
  receiptId?: string;
  receiptNo?: string;
}

interface AmortisationSummary {
  totalPeriods: number;
  paidPeriods: number;
  unpaidPeriods: number;
  totalInstalment: number;
  totalInterest: number;
  totalPrincipal: number;
  totalPaid: number;
  outstandingBalance: number;
  nextDueDate?: string;
  nextDueAmount?: number;
}

const AmortisationSchedule: React.FC = () => {
  const router = useRouter();
  const [debtors, setDebtors] = useState<Debtor[]>([]);
  const [selectedDebtor, setSelectedDebtor] = useState<Debtor | null>(null);
  const [schedule, setSchedule] = useState<AmortisationEntry[]>([]);
  const [summary, setSummary] = useState<AmortisationSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [openGenerateDialog, setOpenGenerateDialog] = useState(false);
  const [generateParams, setGenerateParams] = useState({
    principal: 0,
    monthlyInstalment: 0,
    startDate: new Date().toISOString().split("T")[0],
    monthlyRate: 0.0125,
  });

  useEffect(() => {
    fetchDebtors();
  }, []);

  const fetchDebtors = async () => {
    try {
      const token = localStorage.getItem("vms_access_token");
      const response = await fetch(`${config.API_BASE_URL}/debtors`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setDebtors(data);
      }
    } catch (error) {
      console.error("Error fetching debtors:", error);
    }
  };

  const fetchSchedule = async (debtorId: string) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("vms_access_token");

      // Fetch schedule
      const scheduleRes = await fetch(
        `${config.API_BASE_URL}/amortisation/${debtorId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (scheduleRes.ok) {
        const data = await scheduleRes.json();
        setSchedule(data);
      }

      // Fetch summary
      const summaryRes = await fetch(
        `${config.API_BASE_URL}/amortisation/summary/${debtorId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (summaryRes.ok) {
        const data = await summaryRes.json();
        setSummary(data);
      }
    } catch (error) {
      console.error("Error fetching schedule:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDebtorSelect = (debtor: Debtor | null) => {
    setSelectedDebtor(debtor);
    if (debtor) {
      fetchSchedule(debtor.id);
      setGenerateParams({
        principal: debtor.purchasePrice - debtor.depositPaid,
        monthlyInstalment: debtor.monthlyInstalment || 0,
        startDate: debtor.firstPaymentDate
          ? new Date(debtor.firstPaymentDate).toISOString().split("T")[0]
          : new Date().toISOString().split("T")[0],
        monthlyRate: debtor.interestRate || 0.0125,
      });
    } else {
      setSchedule([]);
      setSummary(null);
    }
  };

  const handleGenerateSchedule = async () => {
    if (!selectedDebtor) return;

    try {
      setGenerating(true);
      const token = localStorage.getItem("vms_access_token");
      const response = await fetch(
        `${config.API_BASE_URL}/amortisation/generate/${selectedDebtor.id}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(generateParams),
        },
      );

      if (response.ok) {
        setOpenGenerateDialog(false);
        await fetchSchedule(selectedDebtor.id);
      }
    } catch (error) {
      console.error("Error generating schedule:", error);
    } finally {
      setGenerating(false);
    }
  };

  const handleExport = () => {
    if (!schedule.length || !selectedDebtor) return;

    const headers = [
      "Period Date",
      "Opening Balance",
      "Instalment",
      "Interest",
      "Principal",
      "Closing Balance",
      "Paid",
      "Amount Paid",
      "Receipt No",
    ];

    const csvRows = schedule.map((row) =>
      [
        new Date(row.periodDate).toLocaleDateString(),
        row.openingBalance.toFixed(2),
        row.instalment.toFixed(2),
        row.interest.toFixed(2),
        row.principal.toFixed(2),
        row.closingBalance.toFixed(2),
        row.paid ? "Yes" : "No",
        row.amountPaid.toFixed(2),
        row.receiptNo || "",
      ].join(","),
    );

    const csv = [headers.join(","), ...csvRows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `amortisation-${selectedDebtor.fullName.replace(
      /\s/g,
      "_",
    )}.csv`;
    a.click();
  };

  const progressPercent = summary
    ? (summary.paidPeriods / summary.totalPeriods) * 100
    : 0;

  return (
    <FinanceContainer>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton onClick={() => router.push("/finance")}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" fontWeight="bold">
              Amortisation Schedule
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            {selectedDebtor && schedule.length > 0 && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleExport}
                >
                  Export CSV
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenGenerateDialog(true)}
                >
                  Regenerate
                </Button>
              </>
            )}
          </Box>
        </Box>

        {/* Debtor Selection */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={debtors}
                  getOptionLabel={(option) =>
                    `${option.fullName} - ${option.unitRef || "N/A"} (${
                      option.developmentName || "N/A"
                    })`
                  }
                  value={selectedDebtor}
                  onChange={(_e, value) => handleDebtorSelect(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Debtor"
                      placeholder="Search by name or unit..."
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                {selectedDebtor && !schedule.length && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenGenerateDialog(true)}
                  >
                    Generate Schedule
                  </Button>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        {summary && (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ScheduleIcon color="primary" />
                    <Typography variant="subtitle2" color="text.secondary">
                      Total Periods
                    </Typography>
                  </Box>
                  <Typography variant="h4" fontWeight="bold" sx={{ mt: 1 }}>
                    {summary.totalPeriods}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {summary.paidPeriods} paid / {summary.unpaidPeriods}{" "}
                    remaining
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={progressPercent}
                    sx={{ mt: 1 }}
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AccountBalanceIcon color="success" />
                    <Typography variant="subtitle2" color="text.secondary">
                      Total Paid
                    </Typography>
                  </Box>
                  <Typography
                    variant="h4"
                    fontWeight="bold"
                    sx={{ mt: 1, color: "#2e7d32" }}
                  >
                    {formatCurrency(summary.totalPaid)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    of {formatCurrency(summary.totalInstalment)} total
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TrendingUpIcon color="warning" />
                    <Typography variant="subtitle2" color="text.secondary">
                      Total Interest
                    </Typography>
                  </Box>
                  <Typography
                    variant="h4"
                    fontWeight="bold"
                    sx={{ mt: 1, color: "#ed6c02" }}
                  >
                    {formatCurrency(summary.totalInterest)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Rate: {(generateParams.monthlyRate * 100).toFixed(2)}%
                    monthly
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AccountBalanceIcon color="error" />
                    <Typography variant="subtitle2" color="text.secondary">
                      Outstanding
                    </Typography>
                  </Box>
                  <Typography
                    variant="h4"
                    fontWeight="bold"
                    sx={{ mt: 1, color: "#d32f2f" }}
                  >
                    {formatCurrency(summary.outstandingBalance)}
                  </Typography>
                  {summary.nextDueDate && (
                    <Typography variant="body2" color="text.secondary">
                      Next due:{" "}
                      {new Date(summary.nextDueDate).toLocaleDateString()}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Schedule Table */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : schedule.length > 0 ? (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#1C4697" }}>
                  <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                    #
                  </TableCell>
                  <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                    Period Date
                  </TableCell>
                  <TableCell
                    sx={{ color: "white", fontWeight: "bold" }}
                    align="right"
                  >
                    Opening Balance
                  </TableCell>
                  <TableCell
                    sx={{ color: "white", fontWeight: "bold" }}
                    align="right"
                  >
                    Instalment
                  </TableCell>
                  <TableCell
                    sx={{ color: "white", fontWeight: "bold" }}
                    align="right"
                  >
                    Interest
                  </TableCell>
                  <TableCell
                    sx={{ color: "white", fontWeight: "bold" }}
                    align="right"
                  >
                    Principal
                  </TableCell>
                  <TableCell
                    sx={{ color: "white", fontWeight: "bold" }}
                    align="right"
                  >
                    Closing Balance
                  </TableCell>
                  <TableCell
                    sx={{ color: "white", fontWeight: "bold" }}
                    align="center"
                  >
                    Status
                  </TableCell>
                  <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                    Receipt
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {schedule.map((row, index) => (
                  <TableRow
                    key={row.id}
                    sx={{
                      backgroundColor: row.paid
                        ? "#e8f5e9"
                        : index % 2 === 0
                        ? "#f5f5f5"
                        : "white",
                    }}
                  >
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      {new Date(row.periodDate).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(row.openingBalance)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(row.instalment)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(row.interest)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(row.principal)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(row.closingBalance)}
                    </TableCell>
                    <TableCell align="center">
                      {row.paid ? (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Paid"
                          size="small"
                          color="success"
                        />
                      ) : (
                        <Chip label="Pending" size="small" color="warning" />
                      )}
                    </TableCell>
                    <TableCell>{row.receiptNo || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : selectedDebtor ? (
          <Alert severity="info">
            No amortisation schedule found for this debtor. Click "Generate
            Schedule" to create one.
          </Alert>
        ) : (
          <Alert severity="info">
            Select a debtor to view their amortisation schedule.
          </Alert>
        )}

        {/* Generate Dialog */}
        <Dialog
          open={openGenerateDialog}
          onClose={() => setOpenGenerateDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Generate Amortisation Schedule</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Principal Amount"
                  type="number"
                  value={generateParams.principal}
                  onChange={(e) =>
                    setGenerateParams({
                      ...generateParams,
                      principal: parseFloat(e.target.value) || 0,
                    })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Monthly Instalment"
                  type="number"
                  value={generateParams.monthlyInstalment}
                  onChange={(e) =>
                    setGenerateParams({
                      ...generateParams,
                      monthlyInstalment: parseFloat(e.target.value) || 0,
                    })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={generateParams.startDate}
                  onChange={(e) =>
                    setGenerateParams({
                      ...generateParams,
                      startDate: e.target.value,
                    })
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Monthly Interest Rate"
                  type="number"
                  inputProps={{ step: 0.0001 }}
                  value={generateParams.monthlyRate}
                  onChange={(e) =>
                    setGenerateParams({
                      ...generateParams,
                      monthlyRate: parseFloat(e.target.value) || 0,
                    })
                  }
                  helperText="e.g., 0.0125 for 1.25% monthly"
                />
              </Grid>
            </Grid>
            {schedule.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Warning: This will replace the existing schedule for this
                debtor.
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenGenerateDialog(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleGenerateSchedule}
              disabled={generating}
            >
              {generating ? <CircularProgress size={24} /> : "Generate"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </FinanceContainer>
  );
};

export default AmortisationSchedule;
